import React, { FCC, useEffect, useState, useMemo } from 'react'
import { useRouteMatch } from 'react-router'

import { VersionFragment } from '@sketch/gql-types'
import { IS_EMBEDDED } from '@sketch/constants'
import { shortcuts } from '@sketch/utils'
import { versionedRoutes } from '@sketch/modules-common'
import { ShareWithoutVersion, useVersioning } from '../../../../versioning'
import {
  useForTablet,
  MenuIconButton,
  TooltipShortcut,
  DocumentHead,
} from '@sketch/components'

import { useHasNewNotifications } from '../../../../annotations/hooks'

import HeaderNavigation from '../../../components/HeaderNavigation'
import { HeaderNavigationSectionInfo } from '../../../components/HeaderNavigation/types'
import VersionLinkButton from '../../../components/VersionLinkButton'
import DerivedHeaderActions from '../../../components/DerivedHeaderActions'

import { NavbarItem, Wrapper } from './SectionDetailHeader.styles'
import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'

import { ReactComponent as ChatBubbles } from '@sketch/icons/chat-bubbles-24'
import { ReactComponent as CodeBlock } from '@sketch/icons/code-block-24'
import { ReactComponent as ClockReWind } from '@sketch/icons/clock-rewind-24'

import {
  useShareSidebarTab,
  getActivePanels,
} from '../../../components/ShareSidebarTabContext'
import { VerticalDivider } from '../../../components/NavbarItem'

type SectionDetailHeaderProps = {
  share: ShareWithoutVersion
  sectionInfo: HeaderNavigationSectionInfo
  isViewingLatestVersion: boolean
  userCanOpenInApp: boolean
  currentVersion: VersionFragment | null
} & Pick<
  DocumentSidebarLayoutExtraProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

/**
 * Header for the single-Section focus view. Mirrors FrameDetailHeader (no
 * search, no share button, no About tab) - the Section equivalent of a
 * focused single-entity page.
 */
export const SectionDetailHeader: FCC<SectionDetailHeaderProps> = ({
  share,
  sectionInfo,
  toggleSidebarRight,
  isSidebarRightOpen,
  isViewingLatestVersion,
  userCanOpenInApp,
  currentVersion,
}) => {
  const { identifier, userCanInspect, versionTypeAvailableToUser } = share
  const { path } = useRouteMatch()
  const isTabletAndBigger = useForTablet()
  const { latestVersionId } = useVersioning()

  const activePanels = useMemo(
    () =>
      getActivePanels({
        path,
        canInspect: userCanInspect,
        // Even when the comments are not allowed, the comments panel should be available
        canComment: true,
      }),
    [path, userCanInspect]
  )

  /**
   * Save the tabs the user has access and
   * update if "shouldShowCommentTab" and "shouldShowInspector" updates
   */
  const [segments, setSegments] = useState(activePanels)

  useEffect(() => {
    setSegments(activePanels)
  }, [activePanels])

  const { activeSegment, toggleSegment } = useShareSidebarTab(segments, {
    isSidebarRightOpen,
    toggleSidebarRight,
  })

  const hasNewNotifications = useHasNewNotifications({
    skip: !share.workspace.userIsMember,
  })

  const showLatestVersionButton =
    isTabletAndBigger &&
    !isViewingLatestVersion &&
    !IS_EMBEDDED &&
    // never show if user can only access latest version (it will update automatically)
    versionTypeAvailableToUser !== 'LATEST'

  return (
    <>
      {sectionInfo.name && (
        <DocumentHead title={`${sectionInfo.name} - ${share.name}`} />
      )}

      <HeaderNavigation share={share} sectionInfo={sectionInfo} />

      <DerivedHeaderActions
        share={share}
        currentVersion={currentVersion}
        userCanOpenInApp={userCanOpenInApp}
        isViewingLatestVersion={isViewingLatestVersion}
      />
      <Wrapper>
        {showLatestVersionButton && (
          <NavbarItem>
            <VersionLinkButton
              to={versionedRoutes.PAGE_SECTION.VERSION.create({
                shareID: identifier,
                pageUUID: sectionInfo.pageUUID,
                sectionUUID: sectionInfo.uuid,
                versionShortId: latestVersionId,
              })}
            >
              See Latest Version
            </VersionLinkButton>
          </NavbarItem>
        )}
        {isTabletAndBigger && (
          <>
            <VerticalDivider />
            <NavbarItem data-testid="panel-triggers-header">
              <MenuIconButton
                description={
                  <>
                    Versions{' '}
                    <TooltipShortcut>
                      Press {shortcuts.version.toUpperCase()}
                    </TooltipShortcut>
                  </>
                }
                aria-current={activeSegment === 'Version'}
                onClick={() => toggleSegment('Version')}
              >
                <ClockReWind role="img" />
                <span className="sr-only">Versions</span>
              </MenuIconButton>

              {userCanInspect && (
                <MenuIconButton
                  description={
                    <>
                      Inspect{' '}
                      <TooltipShortcut>
                        Press {shortcuts.inspect.toUpperCase()}
                      </TooltipShortcut>
                    </>
                  }
                  aria-current={activeSegment === 'Inspect'}
                  onClick={() => toggleSegment('Inspect')}
                >
                  <CodeBlock role="img" />
                  <span className="sr-only">Inspect</span>
                </MenuIconButton>
              )}

              <MenuIconButton
                description={
                  <>
                    Comments{' '}
                    <TooltipShortcut>
                      Press {shortcuts.comment.toUpperCase()}
                    </TooltipShortcut>
                  </>
                }
                data-testid="comment-tab"
                aria-current={activeSegment === 'Comment'}
                onClick={() => toggleSegment('Comment')}
                $isHighlighted={hasNewNotifications}
              >
                <ChatBubbles role="img" />
                <span className="sr-only">Comments</span>
              </MenuIconButton>
            </NavbarItem>
          </>
        )}
      </Wrapper>
    </>
  )
}
